import {
	AppType,
	CalendarDestinationApp
} from "client/Api/Apps/common";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration } from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useState
} from "react";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	FormItem,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	SaveCalendarAppModel,
	EditCalendarAppViewModel,
	saveCalendarApp
} from "client/Api/Apps/EditApp/saveApp";

const validation = createValidationConfiguration<SaveCalendarAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export type EditCalendarAppProps = {
	initialSettings: EditAppSettings<EditCalendarAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditCalendarApp = forwardRef(
	(
		props: EditCalendarAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveCalendarAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const {
			canBeConfigured
		} = app;

		const formHandler: IFormHandler<SaveCalendarAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const prepareModelForSaving = useCallback(
			(model: SaveCalendarAppModel): SaveCalendarAppModel => {
				return {
					...model,
					destinationApp: canBeConfigured
						? model.destinationApp
						: null
				};
			},
			[canBeConfigured]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveCalendarAppModel>(
			formHandler,
			saveCalendarApp,
			AppType.Office365Calendar,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("destinationApp", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				{canBeConfigured && (
					<FormItem
						label={localize("_DefaultClient")}
					>
						<Select
							{...formHandler.for("destinationApp")
								.attachToSelect()
							}
							options={calendarDestinationApps}
							menuTitle={localize("_DefaultClient")}
						/>
						<ValidationError
							{...formHandler.for("destinationApp")
								.attachToValidationError()
							}
						/>
					</FormItem>
				)}
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
			</EditAppBase>
		);
	}
);

EditCalendarApp.displayName = "EditCalendarApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditCalendarAppViewModel>
): SaveCalendarAppModel {
	const {
		destinationWindow,
		destinationApp
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		destinationWindow,
		destinationApp
	};
}

const calendarDestinationApps: readonly SelectItem<CalendarDestinationApp>[] = [
	{ text: localize("_CalendarApp"), value: CalendarDestinationApp.Workspace },
	{ text: localize("_OutlookWebApp"), value: CalendarDestinationApp.OutlookWebApp }
];